import { useState, useEffect, useMemo } from "react";
import MainAccordion from "../Accordion/MainAccordion";
import Box from "@mui/material/Box";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputLabel,
  Stack,
  Typography,
} from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import MenuItem from "@mui/material/MenuItem";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { useParams } from "react-router";
import { useOutletContext } from "react-router-dom";
import { toast } from "react-toastify";

import { useEcrfContext } from "../../context/EcrfContext";
import useAdjustVisitDatesMutation from "../../hooks/useAdjustVisitDatesMutation";
import useChangeInAppVisitStatusMutation from "../../hooks/useChangeInAppVisitStatusMutation";
import useChangeVisitStatusMutation from "../../hooks/useChangeVisitStatusMutation";
import useUpdateSubjectStatusMutation from "../../hooks/useUpdateSubjectStatusMutation";
import CustomButton from "../UI/CustomButton";
import useGetSinglePatient from "../../hooks/useGetSinglePatient";
import { SUBJECT_STATUSES, USER_ROLES, VISIT_STATUSES } from "../../utils/constants";
import useUserActionsLogsMutation from "../../hooks/useUserActionsLogsMutation";
import useGetVisitSchedule from "../../hooks/useGetVisitSchedule";
import { useQueryClient } from "@tanstack/react-query";
import { CustomDatePicker } from "../UI/CustomDatePicker";

dayjs.extend(utc);

const ScheduleVisits = ({ setScheduleBaselineModalShow, singlePhotoSession, embedded = false }) => {
  const { _id_ } = useParams();

  const queryClient = useQueryClient();

  const { selectedVisit, patientId, trial } = useEcrfContext();
  const isTrialLocked = ["Completed", "Archived"].includes(trial?.status);
  const { trialFilter } = useOutletContext() ?? {};

  const { data: infoData } = useGetSinglePatient({
    patientId,
    trialId: trialFilter,
    enabled: !!trialFilter,
  });
  const logAction = useUserActionsLogsMutation();
  const changeVisitStatusMutation = useChangeVisitStatusMutation();
  const changeInAppVisitStatusMutation = useChangeInAppVisitStatusMutation();
  const updateSubjectStatusMutation = useUpdateSubjectStatusMutation();
  const adjustVisitDatesMutation = useAdjustVisitDatesMutation({
    visitId: selectedVisit?.id,
    patientId,
  });

  const user_data = JSON.parse(localStorage.getItem("user_data"));
  const role = user_data.role;

  const { data: visitScheduleData } = useGetVisitSchedule({
    visitId: selectedVisit?.id,
    patientId: patientId,
  });

  const [initialStatusObject, setInitialStatusObject] = useState(null);
  const [initialPatientStatus, setInitialPatientStatus] = useState(null);

  const [statusObject, setStatusObject] = useState({
    visitStartDate: dayjs.utc(visitScheduleData?.visit_start_date) || null,
    visitCompletedDate: dayjs.utc(visitScheduleData?.visit_completed_date) || null,
    inClinicStartDate: dayjs.utc(visitScheduleData?.inclinic_start_date) || null,
    inClinicCompletedDate: dayjs.utc(visitScheduleData?.inclinic_completed_date) || null,
    inClinicVisitStatus: visitScheduleData?.inclinic_visit_status || "",
  });
  const [updatePatientStatus, setUpdatePatientStatus] = useState(null);
  const [statusChanged, setStatusChanged] = useState(false);
  const [showUpdateSubjectStatusPrompt, setShowUpdateSubjectStatusPrompt] = useState(false);
  const [showVisitStartDatePrompt, setShowVisitStartDatePrompt] = useState(false);
  const [showInAppIncompleteConfirm, setShowInAppIncompleteConfirm] = useState(false);
  const [inAppVisitStatus, setInAppVisitStatus] = useState(null);
  const [initialInAppVisitStatus, setInitialInAppVisitStatus] = useState(null);

  const isVisitWindowOpen =
    visitScheduleData?.visit_start_date &&
    visitScheduleData?.visit_completed_date &&
    dayjs.utc().isAfter(dayjs.utc(visitScheduleData.visit_start_date)) &&
    dayjs.utc().isBefore(dayjs.utc(visitScheduleData.visit_completed_date));

  const canChangeInAppToIncomplete =
    (role === USER_ROLES.INVESTIGATOR || role === USER_ROLES.CRC) &&
    visitScheduleData?.inapp_visit_status === VISIT_STATUSES.COMPLETED &&
    isVisitWindowOpen;

  const handleConfirmInAppIncomplete = () => {
    changeInAppVisitStatusMutation.mutate(
      {
        payload: {
          patientId: patientId,
          trialId: trial?.id,
          visitNumber: selectedVisit?.visit_number,
        },
      },
      {
        onSuccess: () => {
          toast.success("App visit marked as Incomplete.");
          setShowInAppIncompleteConfirm(false);
        },
        onError: (err) => {
          const msg = err?.response?.data?.error || "Failed to update app visit status.";
          toast.error(msg);
          setShowInAppIncompleteConfirm(false);
        },
      }
    );
  };

  const handleChange = (status) => {
    setUpdatePatientStatus(status);
    setStatusChanged(true);
  };

  const hasVisitChanges = useMemo(() => {
    if (!initialStatusObject) return false;

    return Object.keys(initialStatusObject).some(
      (key) => initialStatusObject[key] !== statusObject[key]
    );
  }, [statusObject, initialStatusObject]);

  const hasSubjectStatusChange = initialPatientStatus !== updatePatientStatus;

  const hasDatesChanged =
    initialStatusObject?.visitStartDate !== statusObject?.visitStartDate ||
    initialStatusObject?.visitCompletedDate !== statusObject?.visitCompletedDate;

  const hasInAppStatusChange = inAppVisitStatus !== initialInAppVisitStatus;

  const hasAnyChange = hasVisitChanges || hasSubjectStatusChange || hasInAppStatusChange;

  useEffect(() => {
    if (infoData?.patient_status) {
      setUpdatePatientStatus(infoData.patient_status);
      setInitialPatientStatus(infoData.patient_status);
    }
  }, [infoData]);

  useEffect(() => {
    if (!visitScheduleData) return;

    const initial = {
      visitStartDate: visitScheduleData.visit_start_date
        ? dayjs.utc(visitScheduleData.visit_start_date)
        : null,
      visitCompletedDate: visitScheduleData.visit_completed_date
        ? dayjs.utc(visitScheduleData.visit_completed_date)
        : null,
      inClinicStartDate: visitScheduleData.inclinic_start_date
        ? dayjs.utc(visitScheduleData.inclinic_start_date)
        : null,
      inClinicCompletedDate: visitScheduleData.inclinic_completed_date
        ? dayjs.utc(visitScheduleData.inclinic_completed_date)
        : null,
      inClinicVisitStatus: visitScheduleData.inclinic_visit_status || "Not Opened",
    };

    if (Object.keys(initial).length > 0) {
      setStatusObject((prev) => ({ ...prev, ...initial }));
    }
    setInitialStatusObject(initial);

    const serverInAppStatus = visitScheduleData.inapp_visit_status || null;
    setInAppVisitStatus(serverInAppStatus);
    setInitialInAppVisitStatus(serverInAppStatus);
  }, [visitScheduleData]);

  const checkSaveVisitSubjectStatus = () => {
    if (hasInAppStatusChange) {
      setShowInAppIncompleteConfirm(true);
      return;
    }

    if (canAdjustDates && hasDatesChanged) {
      handleAdjustDates();
    }

    if (visitScheduleData?.has_inclinic_activity) {
      if (selectedVisit?.visit_number !== -3) {
        if (
          dayjs
            .utc(statusObject?.visitStartDate)
            .isAfter(dayjs.utc(statusObject?.inClinicStartDate))
        ) {
          if (trial?.enforce_visit_protocol) {
            toast.error("In-Clinic Visit Start Date cannot be set before the Visit Start Date");
            return;
          } else {
            setShowVisitStartDatePrompt(true);
          }
        } else {
          handleSaveVisitStatus();
        }
      } else {
        handleSaveVisitStatus();
      }
    }

    if (
      updatePatientStatus !== SUBJECT_STATUSES.PRE_SCREENING &&
      updatePatientStatus !== SUBJECT_STATUSES.CONTINUING &&
      statusChanged
    ) {
      setShowUpdateSubjectStatusPrompt(true);
    } else {
      handleSubjectStatus();
    }
  };

  const handleSaveVisitStatus = () => {
    setShowVisitStartDatePrompt(false);

    const data = {
      patientId: patientId,
      trialId: trial?.id,
      visitNumber: selectedVisit?.visit_number,
      status: statusObject?.inClinicVisitStatus,
      inClinicStartDate: visitScheduleData?.has_inclinic_activity
        ? statusObject?.inClinicStartDate
        : null,
      inClinicCompletedDate: visitScheduleData?.has_inclinic_activity
        ? statusObject?.inClinicCompletedDate
        : null,
      visitOpenDate: statusObject?.visitStartDate,
      onlyInAppActivity:
        visitScheduleData?.has_inapp_activity && !visitScheduleData?.has_inclinic_activity,
    };

    if (visitScheduleData?.has_inclinic_activity) {
      if (
        !statusObject?.inClinicStartDate &&
        statusObject?.inClinicVisitStatus === VISIT_STATUSES.COMPLETED
      ) {
        toast.error("In-Clinic Visit Start Date cannot be empty");
        return;
      }

      if (!statusObject?.inClinicStartDate && statusObject?.inClinicCompletedDate) {
        toast.error("Please provide an In-Clinic Visit Start Date when an End Date is entered");
        return;
      }

      if (
        statusObject?.inClinicVisitStatus === VISIT_STATUSES.COMPLETED &&
        !statusObject?.inClinicCompletedDate
      ) {
        toast.error("In-Clinic Visit End Date cannot be empty");
        return;
      }

      if (dayjs.utc(data.inClinicStartDate).isAfter(dayjs.utc(data.inClinicCompletedDate))) {
        toast.error("In-Clinic Visit End Date cannot be Past In-Clinic Visit Start Date");
        return;
      }
    }

    changeVisitStatusMutation.mutate(
      {
        payload: data,
      },
      {
        onSuccess: () => {
          toast.success("Visit Status Changed Successfully");
          logAction.mutate({
            action_type: `Visit Status changed, Visit: ${selectedVisit?.visit_name}, ${
              visitScheduleData?.has_inclinic_activity &&
              "In-Clinic Start Date:" +
                new Date(data.inClinicStartDate).toLocaleString() +
                ", In-Clinic End Date:" +
                new Date(data.inClinicCompletedDate).toLocaleString() +
                ","
            } In-Clinic Status: ${data.status}`,
            trial_name: trial.name,
            patient_id: infoData?.email,
          });

          if (!statusChanged || infoData?.patient_status === updatePatientStatus) {
            queryClient.invalidateQueries({ queryKey: ["single_patient"] });
          }
        },
        onError: (err) => {
          toast.error(err?.response?.data?.error || "Failed to change visit status");

          return;
        },
      }
    );
  };

  const handleSubjectStatus = () => {
    if (!statusChanged || infoData?.patient_status === updatePatientStatus) {
      return;
    }

    setShowUpdateSubjectStatusPrompt(false);
    setStatusChanged(false);

    const statusData = {
      patient_id: _id_,
      visit_id: selectedVisit?.id,
      subject_status: updatePatientStatus,
    };

    updateSubjectStatusMutation.mutate(
      {
        payload: statusData,
      },
      {
        onSuccess: () => {
          logAction.mutate({
            action_type: `Subject Status changed, Visit: ${selectedVisit?.visit_name}, Subject Status: ${statusData.subject_status}`,
            trial_name: trial.name,
            patient_id: infoData?.email,
          });
        },
        onError: (err) => {
          toast.error(err?.response || "Failed to update subject status");
          return;
        },
      }
    );
  };

  const canAdjustDates =
    !trial?.enforce_visit_protocol &&
    (role === "Investigator" || role === "Inv_coordinator") &&
    !!visitScheduleData?.vpp_id;

  const handleAdjustDates = () => {
    if (!statusObject?.visitStartDate || !statusObject?.visitCompletedDate) {
      toast.error("Both Visit Open Date and Visit Close Date are required.");
      return;
    }

    if (
      !dayjs.utc(statusObject.visitStartDate).isBefore(dayjs.utc(statusObject.visitCompletedDate))
    ) {
      toast.error("Visit Open Date must be before Visit Close Date.");
      return;
    }

    adjustVisitDatesMutation.mutate(
      {
        vppId: visitScheduleData.vpp_id,
        open_date: dayjs.utc(statusObject.visitStartDate).toISOString(),
        close_date: dayjs.utc(statusObject.visitCompletedDate).toISOString(),
      },
      {
        onSuccess: () => {
          logAction.mutate({
            action_type: `Visit dates adjusted, Visit: ${selectedVisit?.visit_name}, Open: ${dayjs.utc(statusObject.visitStartDate).toISOString()}, Close: ${dayjs.utc(statusObject.visitCompletedDate).toISOString()}`,
            trial_name: trial.name,
            patient_id: infoData?.email,
          });
        },
        onError: (err) => {
          toast.error(err?.response?.data?.error || "Failed to adjust visit dates.");
        },
      }
    );
  };

  const content = (
    <Box sx={{ pb: embedded ? 0 : 2 }} component="form" noValidate autoComplete="off">
      <Stack gap={1}>
        <Stack
          direction="row"
          gap={1}
          alignItems="center"
          sx={{
            width: "100%",
            flexWrap: "wrap",
          }}
        >
          {selectedVisit?.visit_number !== -3 && (
            <>
              <Box sx={{ flex: "0 0 auto", width: 175 }}>
                <CustomDatePicker
                  id="visit_open_date_input"
                  label={
                    visitScheduleData?.is_manually_adjusted
                      ? "Visit Open Date *"
                      : "Visit Open Date"
                  }
                  disabled={
                    !canAdjustDates &&
                    !(
                      (role === "Investigator" || role === "Inv_coordinator") &&
                      selectedVisit?.visit_number === 0
                    )
                  }
                  value={statusObject?.visitStartDate}
                  onChange={(val) =>
                    setStatusObject((prev) => ({
                      ...prev,
                      visitStartDate: val,
                    }))
                  }
                  mode="datetime"
                />
              </Box>

              <Box sx={{ flex: "0 0 auto", width: 175 }}>
                <CustomDatePicker
                  id="visit_close_date_input"
                  label="Visit Close Date"
                  disabled={!canAdjustDates}
                  value={statusObject?.visitCompletedDate}
                  onChange={(val) =>
                    setStatusObject((prev) => ({
                      ...prev,
                      visitCompletedDate: val,
                    }))
                  }
                  mode="datetime"
                />
              </Box>
            </>
          )}

          {visitScheduleData?.has_inclinic_activity && (
            <>
              <Box sx={{ flex: "0 0 auto", width: 175 }}>
                <CustomDatePicker
                  id="in_clinic_start_date_input"
                  label="In-Clinic Visit Start Date"
                  value={statusObject?.inClinicStartDate}
                  onChange={(val) =>
                    setStatusObject((prev) => ({
                      ...prev,
                      inClinicStartDate: val,
                    }))
                  }
                  mode="datetime"
                />
              </Box>

              <Box sx={{ flex: "0 0 auto", width: 175 }}>
                <CustomDatePicker
                  id="in_clinic_end_date_input"
                  label="In-Clinic Visit End Date"
                  value={statusObject?.inClinicCompletedDate}
                  onChange={(val) => {
                    setStatusObject((prev) => {
                      return {
                        ...prev,
                        inClinicCompletedDate: val,
                      };
                    });
                  }}
                  mode="datetime"
                />
              </Box>

              <Box sx={{ flex: "0 0 auto", width: 175 }}>
                <FormControl fullWidth variant="filled">
                  <InputLabel id="demo-simple-select-filled-label" sx={{ fontSize: "0.9rem" }}>
                    In-Clinic Visit Status
                  </InputLabel>
                  <Select
                    value={statusObject?.inClinicVisitStatus}
                    labelId="demo-simple-select-filled-label"
                    id="demo-simple-select-filled"
                    sx={{ fontSize: "0.8rem" }}
                    label="Status"
                    size="small"
                    SelectDisplayProps={{ "data-testid": "inclinic-status-select-trigger" }}
                    onChange={(event) =>
                      setStatusObject((prev) => {
                        return {
                          ...prev,
                          inClinicVisitStatus: event.target.value,
                        };
                      })
                    }
                  >
                    {statusObject?.inClinicVisitStatus === "Not Opened" && (
                      <MenuItem value={"Not Opened"}>Not Opened</MenuItem>
                    )}
                    <MenuItem value={"InComplete"}>InComplete</MenuItem>
                    <MenuItem value={"Skipped"}>Skipped</MenuItem>
                    <MenuItem value={"Completed"}>Completed</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            </>
          )}

          {visitScheduleData?.has_inapp_activity && (
            <>
              <Box sx={{ flex: "0 0 auto", width: 175 }}>
                <CustomDatePicker
                  id="in_app_start_date_input"
                  label="App Visit Start Date"
                  disabled={true}
                  value={visitScheduleData?.inapp_start_date}
                  mode="datetime"
                />
              </Box>

              <Box sx={{ flex: "0 0 auto", width: 175 }}>
                <CustomDatePicker
                  id="in_app_end_date_input"
                  label="App Visit End Date"
                  disabled={true}
                  value={visitScheduleData?.inapp_completed_date}
                  mode="datetime"
                />
              </Box>

              <Box sx={{ flex: "0 0 auto", width: 175 }}>
                <FormControl fullWidth variant="filled">
                  <InputLabel id="inapp-visit-status-label" sx={{ fontSize: "0.9rem" }}>
                    App Visit Status
                  </InputLabel>
                  <Select
                    value={inAppVisitStatus ?? visitScheduleData?.inapp_visit_status}
                    labelId="inapp-visit-status-label"
                    id="inapp-visit-status-select"
                    size="small"
                    label="Status"
                    disabled={!canChangeInAppToIncomplete}
                    onChange={(e) => setInAppVisitStatus(e.target.value)}
                    sx={{ fontSize: "0.8rem" }}
                  >
                    <MenuItem value={"Not Opened"} disabled={canChangeInAppToIncomplete}>
                      Not Opened
                    </MenuItem>
                    <MenuItem value={"InComplete"}>InComplete</MenuItem>
                    <MenuItem value={"Skipped"} disabled={canChangeInAppToIncomplete}>
                      Skipped
                    </MenuItem>
                    <MenuItem value={"Completed"} disabled={canChangeInAppToIncomplete}>
                      Completed
                    </MenuItem>
                  </Select>
                </FormControl>
              </Box>
            </>
          )}

          {visitScheduleData?.overall_visit_status && (
            <Box sx={{ flex: "0 0 auto", width: 175 }}>
              <FormControl fullWidth variant="filled">
                <InputLabel id="demo-simple-select-filled-label" sx={{ fontSize: "0.9rem" }}>
                  Overall Visit Status
                </InputLabel>
                <Select
                  value={visitScheduleData?.overall_visit_status}
                  labelId="demo-simple-select-filled-label"
                  id="demo-simple-select-filled"
                  size="small"
                  label="Status"
                  disabled={true}
                  sx={{ fontSize: "0.8rem" }}
                >
                  <MenuItem value={"Not Opened"}>Not Opened</MenuItem>
                  <MenuItem value={"InComplete"}>InComplete</MenuItem>
                  <MenuItem value={"Skipped"}>Skipped</MenuItem>
                  <MenuItem value={"Completed"}>Completed</MenuItem>
                </Select>
              </FormControl>
            </Box>
          )}

          <Box sx={{ flex: "0 0 auto", width: 175 }}>
            <FormControl fullWidth variant="filled">
              <InputLabel id="subject_status" sx={{ fontSize: "0.9rem" }}>
                Subject Status
              </InputLabel>

              {selectedVisit?.visit_number === -3
                ? updatePatientStatus && (
                    <Select
                      labelId="subject_status"
                      id="subject_status_input"
                      value={updatePatientStatus}
                      size="small"
                      onChange={(e) => handleChange(e.target.value)}
                      disabled={selectedVisit?.is_baseline_scheduled}
                      sx={{ fontSize: "0.8rem" }}
                      SelectDisplayProps={{ "data-testid": "subject-status-select-trigger" }}
                    >
                      <MenuItem value={SUBJECT_STATUSES.PRE_SCREENING}>
                        {SUBJECT_STATUSES.PRE_SCREENING}
                      </MenuItem>
                      <MenuItem value={SUBJECT_STATUSES.SCREENING_FAILED}>
                        {SUBJECT_STATUSES.SCREENING_FAILED}
                      </MenuItem>
                      <MenuItem value={SUBJECT_STATUSES.CONTINUING}>
                        {SUBJECT_STATUSES.CONTINUING}
                      </MenuItem>
                      <MenuItem value={SUBJECT_STATUSES.WITHDREW}>
                        {SUBJECT_STATUSES.WITHDREW}
                      </MenuItem>

                      {infoData.patient_status === SUBJECT_STATUSES.TERMINATED && (
                        <MenuItem value={SUBJECT_STATUSES.TERMINATED}>
                          {SUBJECT_STATUSES.TERMINATED}
                        </MenuItem>
                      )}

                      {infoData.patient_status === SUBJECT_STATUSES.COMPLETED && (
                        <MenuItem value={SUBJECT_STATUSES.COMPLETED}>
                          {SUBJECT_STATUSES.COMPLETED}
                        </MenuItem>
                      )}
                    </Select>
                  )
                : selectedVisit?.visit_number === 0
                  ? updatePatientStatus && (
                      <Select
                        labelId="subject_status"
                        id="subject_status_input"
                        value={updatePatientStatus}
                        size="small"
                        onChange={(e) => handleChange(e.target.value)}
                        disabled={infoData.patient_status !== SUBJECT_STATUSES.CONTINUING}
                        sx={{ fontSize: "0.8rem" }}
                        SelectDisplayProps={{ "data-testid": "subject-status-select-trigger" }}
                      >
                        <MenuItem value={SUBJECT_STATUSES.CONTINUING}>
                          {SUBJECT_STATUSES.CONTINUING}
                        </MenuItem>
                        <MenuItem value={SUBJECT_STATUSES.WITHDREW}>
                          {SUBJECT_STATUSES.WITHDREW}
                        </MenuItem>
                        <MenuItem value={SUBJECT_STATUSES.TERMINATED}>
                          {SUBJECT_STATUSES.TERMINATED}
                        </MenuItem>
                        {(singlePhotoSession ||
                          infoData.patient_status === SUBJECT_STATUSES.COMPLETED) && (
                          <MenuItem value={SUBJECT_STATUSES.COMPLETED}>
                            {SUBJECT_STATUSES.COMPLETED}
                          </MenuItem>
                        )}
                      </Select>
                    )
                  : updatePatientStatus && (
                      <Select
                        labelId="subject_status"
                        id="subject_status_input"
                        size="small"
                        value={updatePatientStatus}
                        onChange={(e) => handleChange(e.target.value)}
                        disabled={infoData.patient_status !== SUBJECT_STATUSES.CONTINUING}
                        sx={{ fontSize: "0.8rem" }}
                        SelectDisplayProps={{ "data-testid": "subject-status-select-trigger" }}
                      >
                        <MenuItem value={SUBJECT_STATUSES.CONTINUING}>
                          {SUBJECT_STATUSES.CONTINUING}
                        </MenuItem>
                        <MenuItem value={SUBJECT_STATUSES.COMPLETED}>
                          {SUBJECT_STATUSES.COMPLETED}
                        </MenuItem>

                        <MenuItem value={SUBJECT_STATUSES.WITHDREW}>
                          {SUBJECT_STATUSES.WITHDREW}
                        </MenuItem>
                        <MenuItem value={SUBJECT_STATUSES.TERMINATED}>
                          {SUBJECT_STATUSES.TERMINATED}
                        </MenuItem>
                      </Select>
                    )}
            </FormControl>
          </Box>

          {(role === "Investigator" ||
            role === "Inv_coordinator" ||
            role === "Sub_investigator") && (
            <Box sx={{ flexShrink: 0 }}>
              <CustomButton
                data-testid="update-status-btn"
                disabled={!hasAnyChange || isTrialLocked}
                onClick={checkSaveVisitSubjectStatus}
                sx={{
                  position: "relative",
                  overflow: "visible",
                  ...(hasAnyChange && {
                    "&::after": {
                      content: '""',
                      position: "absolute",
                      insetBlock: -4,
                      insetInline: -1,
                      borderRadius: "inherit",
                      border: "6px solid",
                      borderColor: "primary.main",
                      opacity: 0,
                      animation: "pulse 1.6s ease-out infinite",
                      pointerEvents: "none",
                    },
                  }),
                }}
              >
                Update Status
              </CustomButton>
            </Box>
          )}
        </Stack>

        <Typography variant="caption" sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <InfoIcon
            sx={{
              fontSize: "1rem",
              color: "primary.main",
            }}
          />
          The <em>Update Status</em> button will remain disabled until any of the fields are
          changed.
        </Typography>
      </Stack>

      {!trial?.trial_custom_id?.includes("RBL") &&
        ["Investigator", "Inv_coordinator", "Sub_investigator"].includes(role) &&
        selectedVisit?.visit_number === -3 &&
        !selectedVisit?.is_baseline_scheduled &&
        infoData.patient_status === SUBJECT_STATUSES.CONTINUING && (
          <Stack direction="row" alignItems="center" gap={2} sx={{ mt: 1 }}>
            <Typography variant="body2">Would you like to schedule the next visit?</Typography>
            <CustomButton
              size="small"
              data-testid="schedule-baseline-btn"
              onClick={() => setScheduleBaselineModalShow(true)}
            >
              Schedule Baseline Visit
            </CustomButton>
          </Stack>
        )}

      <Dialog
        open={showUpdateSubjectStatusPrompt}
        onClose={() => setShowUpdateSubjectStatusPrompt(false)}
        BackdropProps={{
          sx: {
            backdropFilter: "blur(4px)",
            backgroundColor: "rgba(0, 0, 0, 0.2)",
          },
        }}
      >
        <DialogTitle
          sx={{
            fontWeight: 700,
            color: "red",
          }}
        >
          THIS ACTION CANNOT BE UNDONE
        </DialogTitle>

        <DialogContent>
          <Typography>
            Are you sure you want to update the subject status to{" "}
            <strong>{updatePatientStatus}</strong>?
          </Typography>
        </DialogContent>

        <DialogActions>
          <CustomButton onClick={handleSubjectStatus}>Yes</CustomButton>
          <CustomButton onClick={() => setShowUpdateSubjectStatusPrompt(false)}>No</CustomButton>
        </DialogActions>
      </Dialog>

      <Dialog
        open={showInAppIncompleteConfirm}
        onClose={() => setShowInAppIncompleteConfirm(false)}
        BackdropProps={{
          sx: {
            backdropFilter: "blur(4px)",
            backgroundColor: "rgba(0, 0, 0, 0.2)",
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Confirm App Visit Status Change</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to mark the App Visit as INCOMPLETE?</Typography>
        </DialogContent>
        <DialogActions>
          <CustomButton
            onClick={handleConfirmInAppIncomplete}
            disabled={changeInAppVisitStatusMutation.isPending}
          >
            Yes
          </CustomButton>
          <CustomButton onClick={() => setShowInAppIncompleteConfirm(false)}>No</CustomButton>
        </DialogActions>
      </Dialog>

      <Dialog
        open={showVisitStartDatePrompt}
        onClose={() => setShowVisitStartDatePrompt(false)}
        BackdropProps={{
          sx: {
            backdropFilter: "blur(4px)",
            backgroundColor: "rgba(0, 0, 0, 0.2)",
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: "bold" }}>Confirm In-Clinic Start Date Update</DialogTitle>

        <DialogContent>
          <Typography>
            Are you sure you want to set In-Clinic Visit Start Date before the Visit Start Date?
          </Typography>
        </DialogContent>

        <DialogActions>
          <CustomButton onClick={handleSaveVisitStatus}>Yes</CustomButton>
          <CustomButton onClick={() => setShowVisitStartDatePrompt(false)}>No</CustomButton>
        </DialogActions>
      </Dialog>
    </Box>
  );

  // The v2 eCRF visit card shows these fields inline rather than in their own accordion
  if (embedded) return content;

  return <MainAccordion title="Visit Status" isOpen={!!patientId} data={content} />;
};

export default ScheduleVisits;
